import { useAccountant } from '../../contexts/AccountantContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Buildings,
  ArrowLeft,
  CaretDown,
  ArrowsLeftRight,
  SignOut,
  House,
  Receipt,
  ChartLineUp,
  Users,
  Money,
  CircleNotch
} from '@phosphor-icons/react';
import { cn } from '../../lib/utils';
import { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientWorkspaceHeader() {
  const { 
    activeClient, 
    isInClientContext, 
    exitClientWorkspace, 
    switchClientWorkspace,
    contextLoading,
    branding
  } = useAccountant();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);

  useEffect(() => {
    if (isInClientContext) {
      fetchClients();
    }
  }, [isInClientContext]);

  const fetchClients = async () => {
    setLoadingClients(true);
    try {
      const response = await axios.get(`${API_URL}/accountant/clients`);
      setClients(response.data || []);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    } finally {
      setLoadingClients(false);
    }
  };

  const handleExit = async () => {
    await exitClientWorkspace();
    navigate('/dashboard/accountant');
  };

  const handleSwitch = async (clientId) => {
    await switchClientWorkspace(clientId);
  };

  if (!isInClientContext) {
    return null;
  }

  const otherClients = clients.filter(c => c.id !== activeClient?.client_id);

  return (
    <div 
      className="bg-gradient-to-r from-[#0f172a] to-[#1e3a5f] text-white px-4 py-2 flex items-center justify-between shadow-lg"
      data-testid="client-workspace-header"
    >
      {/* Left: Back button and Client Info */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleExit}
          className="text-white/80 hover:text-white hover:bg-white/10"
          disabled={contextLoading}
          data-testid="exit-client-btn"
        >
          {contextLoading ? (
            <CircleNotch size={16} className="animate-spin mr-2" />
          ) : (
            <ArrowLeft size={16} className="mr-2" />
          )}
          Exit
        </Button>

        <div className="h-6 w-px bg-white/20" />

        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-[#d4a84b] flex items-center justify-center">
            <Buildings size={18} className="text-[#0f172a]" weight="fill" />
          </div>
          <div>
            <p className="text-xs text-white/60 uppercase tracking-wider">Managing Client</p>
            <p className="font-semibold text-white" data-testid="active-client-name">
              {activeClient?.company_name || 'Unknown Client'}
            </p>
          </div>
        </div>
      </div>

      {/* Center: Quick Navigation */}
      <div className="hidden md:flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/dashboard')}
          className="text-white/70 hover:text-white hover:bg-white/10 text-xs"
        >
          <House size={14} className="mr-1" />
          Dashboard
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/dashboard/accounts')}
          className="text-white/70 hover:text-white hover:bg-white/10 text-xs"
        >
          <Money size={14} className="mr-1" />
          Accounts
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/dashboard/sales')}
          className="text-white/70 hover:text-white hover:bg-white/10 text-xs"
        >
          <Receipt size={14} className="mr-1" />
          Sales
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/dashboard/accounts/tax')}
          className="text-white/70 hover:text-white hover:bg-white/10 text-xs"
        >
          <ChartLineUp size={14} className="mr-1" />
          Tax
        </Button>
      </div>

      {/* Right: Switch Client Dropdown */}
      <div className="flex items-center gap-3">
        <Badge className="bg-[#d4a84b] text-[#0f172a] text-xs px-2 py-0.5">
          {branding.appName}
        </Badge>

        {otherClients.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="text-white/80 hover:text-white hover:bg-white/10"
                disabled={contextLoading}
              >
                <ArrowsLeftRight size={16} className="mr-2" />
                Switch Client
                <CaretDown size={14} className="ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <div className="px-2 py-1.5 text-xs text-muted-foreground">
                Switch to another client
              </div>
              <DropdownMenuSeparator />
              {loadingClients ? (
                <div className="px-2 py-4 text-center text-sm text-muted-foreground">
                  <CircleNotch size={16} className="animate-spin mx-auto mb-1" />
                  Loading...
                </div>
              ) : (
                otherClients.slice(0, 10).map((client) => (
                  <DropdownMenuItem
                    key={client.id}
                    onClick={() => handleSwitch(client.id)}
                    className="cursor-pointer"
                  >
                    <Buildings size={16} className="mr-2 text-muted-foreground" />
                    <div className="flex-1 truncate">
                      <span className="font-medium">{client.name}</span>
                      {client.trn && (
                        <span className="text-xs text-muted-foreground ml-1">
                          ({client.trn})
                        </span>
                      )}
                    </div>
                  </DropdownMenuItem>
                ))
              )}
              {otherClients.length > 10 && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => {
                      exitClientWorkspace();
                      navigate('/dashboard/accountant');
                    }}
                    className="cursor-pointer text-muted-foreground"
                  >
                    View all clients...
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}
